import { Currency, CurrencyDeprecated } from '@app/core/models/customer.models';
import { environment } from '@env/environment';
import { AdPlatformAccessStatus, CampaignOptimizationValue } from './core/models/common.models';
import { UserLanguage } from './core/models/user.models';

export const OptimizationTypes = {
  REVENUE: 'REVENUE' as CampaignOptimizationValue,
  CLICK: 'CLICK' as CampaignOptimizationValue,
  PROFIT: 'PROFIT' as CampaignOptimizationValue,
  VCPM: 'VCPM' as CampaignOptimizationValue,
};
export const platforms = { ...environment.app.platforms };
export const platformOrder = [
  platforms.AMAZON.name,
  platforms.WALMART.name,
  platforms.EBAY.name,
  platforms.GOOGLE.name,
  platforms.MICROSOFT.name,
];

export const platformAccess: Record<string, { value: boolean; key: AdPlatformAccessStatus }> = {
  OK: {
    value: true,
    key: 'OK',
  },
  READ_ONLY: {
    value: false,
    key: 'READ-ONLY',
  },
  ACCOUNT_DISABLED: {
    value: false,
    key: 'ACCOUNT-DISABLED',
  },
  PERMISSION_DENIED_ACCOUNT: {
    value: false,
    key: 'PERMISSION-DENIED-ACCOUNT',
  },
  PERMISSION_DENIED_MANAGER: {
    value: false,
    key: 'PERMISSION-DENIED-MANAGER',
  },
  OAUTH_REVOKED_ACCOUNT: {
    value: false,
    key: 'OAUTH-REVOKED-ACCOUNT',
  },
  OAUTH_REVOKED_MANAGER: {
    value: false,
    key: 'OAUTH-REVOKED-MANAGER',
  },
  POLICY_NOT_ENROLLED: {
    value: false,
    key: 'POLICY-NOT-ENROLLED',
  },
};
export const roles = {
  NONE: 0,
  USER: 1,
  CUSTOMER_ADMIN: 10,
  SYS_ADMIN: 1000,
};
export const languages: Record<string, UserLanguage> = { ENGLISH: 'EN', GERMAN: 'DE', CHINESE: 'ZH' };
export const pagination = {
  index: 1,
  size: 25,
  sizeOptions: [5, 10, 25, 50, 100, 200, 500],
};
export const params = {
  REDIRECT: 'redirect',
  IMPERSONATION_ID: 'adspert_impersonation',
  TEST_ID: 'adspert_test',
  CUSTOMER_ID: 'customer_id',
  ADSPERT_ID: 'adspert_id',
  GLOBAL_FILTER_ACCOUNT_QUERY: 'gbl_acct_q',
  GLOBAL_FILTER_CUSTOMTER_PERFORMANCE_GROUP_ID: 'gbl_cpg_id',
  GLOBAL_FILTER_PERFORMANCE_GROUP_ID: 'gbl_pg_id',
  GLOBAL_FILTER_PORTFOLIO_ID: 'gbl_pf_id',
  GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY: 'gbl_pg_q',
  GLOBAL_FILTER_CAMPAIGN_ID: 'gbl_cpgn_id',
  GLOBAL_FILTER_CAMPAIGN_QUERY: 'gbl_cpgn_q',
  GLOBAL_FILTER_AD_GROUPS_ID: 'gbl_ad_id',
  GLOBAL_FILTER_AD_GROUPS_QUERY: 'gbl_ad_q',
  GLOBAL_FILTER_CRITERIA_ID: 'gbl_crit_id',
  GLOBAL_FILTER_CRITERIA_QUERY: 'gbl_crit_q',
  GLOBAL_FILTER_SEARCH_QUERY: 'gbl_query',
  GLOBAL_FILTER_DATE_RANGE: 'gbl_dt',
  GLOBAL_FILTER_DATE_COMPARE: 'gbl_dt_cm',
  GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY: 'gbl_des_pg_q',
  GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID: 'gbl_des_pg_id',
  GLOBAL_FILTER_DEST_CAMPAIGN_ID: 'gbl_des_cpgn_id',
  GLOBAL_FILTER_DEST_CAMPAIGN_QUERY: 'gbl_des_cpgn_q',
  GLOBAL_FILTER_DEST_AD_GROUPS_ID: 'gbl_des_ad_id',
  GLOBAL_FILTER_DEST_AD_GROUPS_QUERY: 'gbl_des_ad_q',
  GLOBAL_FILTER_RULES_QUERY: 'gbl_rules_q',
  MONITOR_OPTI_TYPE: 'opti_type',
  DELAY: 'delay',
  KEY_FIGURE_TAB: 'tab',
  RULES_TAB: 'rules_tab',
  RULE_TYPE_ID: 'rule_type_id',
  EDIT_RULE_TYPE_ID: 'edit_rule_type_id',
  MANAGE_ACCOUNT_QUERY: 'ma_query',
  MANAGE_USERS_QUERY: 'mu_query',
  MANAGE_RETAIL_ACCOUNT_QUERY: 'mra_query',
  ADMIN_CUSTOMERS_QUERY: 'cu_query',
  ADMIN_ACCOUNTS_QUERY: 'ac_query',
  ADMIN_USER_LOG_QUERY: 'ul_query',
  ACCOUNT_GROUP_QUERY: 'ag_query',
  USER_API_KEY_QUERY: 'uak_query',
  MANAGE_PLATFORM_FILTER: 'ma_plat',
  PRODUCTS_STATUS_FILTER: 'p_stat',
  PRODUCT_ID: 'p_id',
  NOTIFICATION_TYPE: 'n_type',
  SIGNUP_PLATFORM: 'platform',
};
export const DEFAULTS = {
  accountsDefaultFilter: '',
  PGDefaultFilter: '', //enabled:true
  campaignsDefaultFilter: 'status:ACTIVE;optimize:true',
  adGroupsDefaultFilter: '', // 'status:ACTIVE',
  criteriaDefaultFilter: '', // 'status:ACTIVE'
};
export const COLORS = {
  PINK: '#bc5090',
  ORANGE: '#ffa600',
  PURPLE: '#58508d',
  RED: '#ff6361',
  GREEN: '#5ec779',
  BLUE: '#0086c2',
  TRANSPARENT: 'rgba(0,0,0,0)',
  PRIMARY_TEXT: '#232b3b',
  SECONDARY_TEXT: '#5D6C7F',
  TOP: '#28B3EC',
  TOP_OPACITY_20: 'rgba(41, 180, 236, 0.18)',
  DARK_BLUE: '#005A8C',
  DARK_BLUE_OPACITY_20: 'rgba(0, 90, 140, 0.18)',
  BRAND: '#009ee0',
  BRAND_OPACITY_20: 'rgba(0, 158, 224, 0.18)',
  BRAND_OPACITY_50: 'rgba(0, 158, 224, 0.5)',
  WHITE: '#fff',
  WHITE_OPACITY_25: 'rgba(255,255,255, 0.25)',
  BLACK: '#000',
  CHART_DISABLE_AREA: 'rgb(228, 232, 240, 0.5)',
  DANGER: '#d63649',
  BORDER: '#e4e8f0',
  OPT_TYPE_TEAL: '#37c5b1',
  OPT_TYPE_VIOLET: '#8541fc',
  OPT_TYPE_GREEN: '#6bcf1c',
  OPT_TYPE_PINK: '#D51FDB',
};
export const AccountVariant = {
  NOT_CONNECTED: 'NOT-CONNECTED',
  OWN_CUSTOMER: 'OWN-CUSTOMER',
  OTHER_CUSTOMER: 'OTHER-CUSTOMER',
};
export const GlobalFilterParams = [
  params.GLOBAL_FILTER_ACCOUNT_QUERY,
  params.GLOBAL_FILTER_CUSTOMTER_PERFORMANCE_GROUP_ID,
  params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID,
  params.GLOBAL_FILTER_PORTFOLIO_ID,
  params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY,
  params.GLOBAL_FILTER_CAMPAIGN_ID,
  params.GLOBAL_FILTER_CAMPAIGN_QUERY,
  params.GLOBAL_FILTER_AD_GROUPS_ID,
  params.GLOBAL_FILTER_AD_GROUPS_QUERY,
  params.GLOBAL_FILTER_CRITERIA_ID,
  params.GLOBAL_FILTER_CRITERIA_QUERY,
  params.GLOBAL_FILTER_SEARCH_QUERY,
  params.GLOBAL_FILTER_DATE_RANGE,
  params.GLOBAL_FILTER_DATE_COMPARE,
  params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY,
  params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID,
  params.GLOBAL_FILTER_DEST_CAMPAIGN_ID,
  params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY,
  params.GLOBAL_FILTER_DEST_AD_GROUPS_ID,
  params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY,
  params.GLOBAL_FILTER_RULES_QUERY,
];

type CurrencyMapKey = Currency | CurrencyDeprecated | 'ALL';

type CurrencyMap<T extends CurrencyMapKey> = Record<
  T,
  { symbol: string; deprecated?: T extends CurrencyDeprecated ? true : false }
>;

export const currencyMap: CurrencyMap<CurrencyMapKey> = {
  ALL: { symbol: 'ALL' },
  HRK: { symbol: 'HRK' },
  USD: { symbol: '$' },
  JPY: { symbol: '¥' },
  EUR: { symbol: '€' },
  GBP: { symbol: '£' },
  AUD: { symbol: 'AU$' },
  CAD: { symbol: 'CA$' },
  CHF: { symbol: 'CHF' },
  DKK: { symbol: 'Dkr' },
  HKD: { symbol: 'HK$' },
  SEK: { symbol: 'Skr' },
  PLN: { symbol: 'zł' },
  INR: { symbol: '₹' },
  MXN: { symbol: 'MK$' },
  TRY: { symbol: '₺' },
  SGD: { symbol: 'S$' },
  BRL: { symbol: 'R$' },
  AED: { symbol: 'AED' },
  EGP: { symbol: 'E£' },
};
export const MAX_BID_DEFAULT_SUGGESTION_INT = 1;

export const NULL_METRIC_SIGN = '—';

export const MAX_BID_MIN_VALUE_INT = 0.02;

export const MAX_BID_MAX_CHANGE_PERCENT = 20 / 100;

const search = new URLSearchParams(new URL(window.location.href).search);

const [test_max_trial_accounts, test_max_customer_accounts] =
  search
    .get('test_customer_account_limit')
    ?.split(',')
    .map((limit) => parseInt(limit, 10)) || [];

export const MAX_TRIAL_ACCOUNTS = Math.min(test_max_trial_accounts ?? Number.POSITIVE_INFINITY, 10);

export const MAX_CUSTOMER_ACCOUNTS = Math.min(test_max_customer_accounts ?? Number.POSITIVE_INFINITY, 200);

export const DEFAULT_PAYMENT_DUE_DAYS = 14;

export const PlatformLinks = {
  EBAY: {
    en: 'https://signin.ebay.com/ws/eBayISAPI.dll?SignIn&ru=https%3A%2F%2Fwww.ebay.com%2Fmys%2Fhome%3Fsource%3DGBH&sgfl=sm&smuid=662c8f5412dc4c9382fb47180f54202c',
    de: 'https://signin.ebay.de/ws/eBayISAPI.dll?SignIn&ru=https%3A%2F%2Fwww.ebay.de%2Fmys%2Fhome%3Fsource%3DGBH&sgfl=sm&smuid=662c8f5412dc4c9382fb47180f54202c',
    zh: 'https://signin.ebay.com/ws/eBayISAPI.dll?SignIn&ru=https%3A%2F%2Fwww.ebay.com%2Fmys%2Fhome%3Fsource%3DGBH&sgfl=sm&smuid=662c8f5412dc4c9382fb47180f54202c',
  },
  GOOGLE: 'https://ads.google.com',
  MICROSOFT: 'https://ads.microsoft.com',
  AMAZON: 'https://advertising.amazon.com',
  WALMART: 'https://advertising.walmart.com/signin',
};
